/**
 * Subscription Service
 */
import { getApiClient } from '../apiClient';
import { SUBSCRIPTION_ENDPOINTS, replacePathParams } from '../endpoints';
import type {
  GetSubscriptionsResponse,
  GetSubscriptionsParams,
  GetSubscriptionResponse,
  UpdateSubscriptionPayload,
  UpdateSubscriptionResponse,
  GetUserSubscriptionResponse,
  GetAllTransactionsResponse,
  GetAllTransactionsParams,
  GetSubscriptionSettingsResponse,
  UpdateSubscriptionSettingsPayload,
  UpdateSubscriptionSettingsResponse,
  GetUserPurchasesResponse,
} from '../types/subscription.types';
import type { PaginationParams, SearchParams } from '../types';

export const subscriptionService = {
  /**
   * Get users who purchased a subscription
   */
  getUserPurchases: (id: string, params?: PaginationParams & SearchParams): Promise<GetUserPurchasesResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.search) queryParams.append('search', params.search);

    const baseUrl = replacePathParams(SUBSCRIPTION_ENDPOINTS.getUserPurchase, { id });
    const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
    return getApiClient().get(url);
  },

  /**
   * Get subscription settings (free trial period)
   */
  getSettings: (): Promise<GetSubscriptionSettingsResponse> => {
    return getApiClient().get(SUBSCRIPTION_ENDPOINTS.getSettings);
  },

  /**
   * Update subscription settings
   */
  updateSettings: (body: UpdateSubscriptionSettingsPayload): Promise<UpdateSubscriptionSettingsResponse> => {
    return getApiClient().put(SUBSCRIPTION_ENDPOINTS.updateSettings, body);
  },

  /**
   * Get all subscriptions with pagination and search
   */
  getSubscriptions: (params?: GetSubscriptionsParams): Promise<GetSubscriptionsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) {
      queryParams.append('page', String(params.page));
    }
    if (params?.limit) {
      queryParams.append('limit', String(params.limit));
    }
    if (params?.offset) {
      queryParams.append('offset', String(params.offset));
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.type) {
      queryParams.append('type', params.type);
    }
    const url = queryParams.toString()
      ? `${SUBSCRIPTION_ENDPOINTS.getSubscriptions}?${queryParams.toString()}`
      : SUBSCRIPTION_ENDPOINTS.getSubscriptions;
    return getApiClient().get(url);
  },

  /**
   * Get single subscription by ID
   */
  getSubscription: (id: string): Promise<GetSubscriptionResponse> => {
    const url = replacePathParams(SUBSCRIPTION_ENDPOINTS.getSubscription, { id });
    return getApiClient().get(url);
  },

  /**
   * Update subscription
   */
  updateSubscription: (id: string, body: UpdateSubscriptionPayload): Promise<UpdateSubscriptionResponse> => {
    const url = replacePathParams(SUBSCRIPTION_ENDPOINTS.updateSubscription, { id });
    return getApiClient().put(url, body);
  },

  /**
   * Get user subscription details by user ID
   */
  getUserSubscription: (userId: string): Promise<GetUserSubscriptionResponse> => {
    const url = replacePathParams(SUBSCRIPTION_ENDPOINTS.getUserSubscription, { id: userId });
    return getApiClient().get(url);
  },

  /**
   * Get all transactions with pagination and search
   */
  getAllTransactions: (params?: GetAllTransactionsParams): Promise<GetAllTransactionsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.search) queryParams.append('search', params.search);

    const url = queryParams.toString()
      ? `${SUBSCRIPTION_ENDPOINTS.getAllTransactions}?${queryParams.toString()}`
      : SUBSCRIPTION_ENDPOINTS.getAllTransactions;
    return getApiClient().get(url);
  },
};

